import styled from 'styled-components';
import { useState, useRef } from 'react';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';

import BREAKPOINTS from 'styles/breakpoints';

import { P1, H6 } from 'styles/typography';

import { CONTACT_EMAIL } from 'utils/constants';

import validation from 'utils/form-validation-schema';

import Button from 'components/Button';
import Input from 'components/Form/Input';
import Link from 'components/Link';
import SelectInput from 'components/Form/SelectInput';

import { postData } from 'utils/api';
import { formatEnumToString } from 'utils/helpers';

import { Grid } from 'styles/grid';

import {
  Container,
  TitleSection,
  Title,
  Form,
  CheckboxStyled,
} from '../ContactForm/styles';

const SuccessMsg = styled.div`
  max-width: 1160px;
  padding-top: 50px;
  margin: 0 var(--gutter-side);

  ${BREAKPOINTS.large`
		margin: auto;
	`}
`;

const ErrorMsg = styled(SuccessMsg)``;

const GTM_IDENTIFIER = 'gtm-whats-on';

const WhatsOnForm = ({ data, sourceId }) => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {},
  });

  const router = useRouter();

  const preferredLocations = data.formPreferredLocationsOptions?.map(
    (locations) => formatEnumToString(locations) || []
  );

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const formSubmitMessageRef = useRef(null);

  const handleOnSubmit = async (formData) => {
    if (!hasAcceptedTerms && !isOver18) return;
    setSubmitting(true);

    try {
      const res = await postData('/api/whats-on', {
        ...formData,
        sourceId: sourceId || 'whats-on-event',
      });

      if (res.success) {
        router.push(`${router.asPath}?success=true`, undefined, {
          shallow: true,
        });
        setSuccess(true);
      }
    } catch (e) {
      setError(true);
    }

    formSubmitMessageRef.current?.scrollIntoView({ block: 'center' });
    setSubmitting(false);
  };

  const hasAcceptedTerms = watch('acceptTerms');
  const isOver18 = watch('over18');

  return (
    <Container theme="light" id="register-interest">
      <Grid>
        <TitleSection>
          <Title html={data.heading?.html} />
        </TitleSection>

        <Form onSubmit={handleSubmit(handleOnSubmit)} noValidate>
          <Input
            label="First name"
            errorMessage={errors.firstName?.message}
            value={watch('firstName')}
            {...register('firstName', validation.firstName)}
          />

          <Input
            label="Last name"
            errorMessage={errors.lastName?.message}
            value={watch('lastName')}
            {...register('lastName', validation.lastName)}
          />

          <Input
            label="Email address"
            type="email"
            errorMessage={errors.email?.message}
            value={watch('email')}
            {...register('email', validation.email)}
          />

          {data.formEventDateOptions && (
            <SelectInput
              label="Select Date"
              placeholder="Select Preferred Date"
              options={data.formEventDateOptions || []}
              errorMessage={errors.preferredDate?.message}
              setValue={setValue}
              {...register('preferredDate', {
                required: 'Please select a date',
              })}
            />
          )}

          {data.formPreferredLocationsOptions && (
            <SelectInput
              label="Select Location"
              placeholder="Select Preferred Location"
              options={preferredLocations || []}
              errorMessage={errors.preferredLocation?.message}
              setValue={setValue}
              {...register('preferredLocation', {
                required: 'Please select a location',
              })}
            />
          )}

          <CheckboxStyled {...register('marketingOptIn')}>
            I am happy for Clays to send me updates on latest happenings and
            special offers.
          </CheckboxStyled>

          <CheckboxStyled {...register('over18')}>I am over 18</CheckboxStyled>

          <CheckboxStyled {...register('acceptTerms')}>
            I have read and accept the terms of processing data in the{' '}
            <Link href="/privacy-policy" targetBlank>
              Privacy Policy.
            </Link>
          </CheckboxStyled>

          <Button
            disabled={!hasAcceptedTerms || !isOver18 || submitting}
            type="submit"
            className={GTM_IDENTIFIER}
          >
            Register interest
          </Button>
        </Form>
      </Grid>

      {success && (
        <SuccessMsg ref={formSubmitMessageRef}>
          <H6>Thank you for pre-registering for our Friday Fever Shootout</H6>
          <P1>
            You&apos;re officially locked and loaded and we can&apos;t wait to
            see you take aim on your chosen Friday from 20:00.
            <br />
            Our shoot captain will be ready and waiting for you on the night so
            get ready to unleash your inner sharpshooter!
          </P1>
        </SuccessMsg>
      )}

      {error && (
        <ErrorMsg ref={formSubmitMessageRef}>
          <H6>Uh oh... we&apos;re sorry</H6>
          <P1>
            Something went wrong. We’re currently unable to handle this request.
            Please email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>{' '}
            to complete your enquiry.
          </P1>
        </ErrorMsg>
      )}
    </Container>
  );
};

export default WhatsOnForm;
